import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, CheckCircle2, Circle, ClipboardList, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { InternalMovementView, MovementTaskView } from '../../../models/employee';
import { ProcessTaskStatus } from '../../../models/employee';
import { updateMovementTaskStatus, getInternalMovement } from '../../../api/Employee/internalMovements';
import { appDate } from '../../../utils/appDateFormat';

interface MovementDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  movement: InternalMovementView | null;
}

const getStatusColor = (status: ProcessTaskStatus): string => {
  switch (status) {
    case ProcessTaskStatus.Completed: return 'bg-green-100 text-green-800';
    case ProcessTaskStatus.Cancelled: return 'bg-red-100 text-red-800';
    case ProcessTaskStatus.InProgress: return 'bg-blue-100 text-blue-800';
    default: return 'bg-yellow-100 text-yellow-800';
  }
};

const getStatusLabel = (status: ProcessTaskStatus): string => {
  switch (status) {
    case ProcessTaskStatus.Completed: return 'Completed';
    case ProcessTaskStatus.Cancelled: return 'Cancelled';
    case ProcessTaskStatus.InProgress: return 'In Progress';
    default: return 'Pending';
  }
};

const MovementDetailsModal: React.FC<MovementDetailsModalProps> = ({ isOpen, onClose, movement }) => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<MovementTaskView[]>([]);
  // The checklist has THREE distinct states, and collapsing them is what produced the defect: this modal
  // used to render the "No checklist items." empty state while the fresh GetById was still in flight, so
  // an HR who added an item in the Edit modal and immediately opened Details was told the checklist was
  // empty — and, if that fetch failed, the catch/else branches overwrote the state with `movement.tasks`
  // from the LIST payload, which the parent had captured BEFORE the add (index.tsx closes the edit modal
  // and only then fires refreshMovements, so the row object in hand is the pre-add snapshot). The stale
  // empty then stuck until the tab was re-navigated, matching the reported 2-of-4 repro.
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Monotonic request token: only the LATEST fetch may write state. Opening details right after a write
  // can leave two GetById calls in flight (the effect re-runs whenever the movement prop identity
  // changes), and responses are not ordered — without this an older payload can land last and win.
  const requestSeq = useRef(0);

  const loadTasks = useCallback(async (movementId: number) => {
    const seq = ++requestSeq.current;
    setIsLoadingTasks(true);
    setLoadFailed(false);
    try {
      const result = await getInternalMovement(movementId);
      if (seq !== requestSeq.current) return; // superseded by a newer request
      if (!result.errors || result.errors.length === 0) {
        // The server response is authoritative — this IS the read-your-own-write refetch.
        setTasks(result.payload?.tasks || []);
      } else {
        setLoadFailed(true);
      }
    } catch {
      if (seq === requestSeq.current) setLoadFailed(true);
    } finally {
      if (seq === requestSeq.current) setIsLoadingTasks(false);
    }
  }, []);

  useEffect(() => {
    if (!movement || !isOpen) return;
    // Seed from the row we already hold so the panel is never blank-by-default, then let the fetch
    // above replace it. A failed fetch keeps this seed rather than clobbering a good list with it.
    setTasks(movement.tasks || []);
    loadTasks(movement.id);
  }, [movement, isOpen, loadTasks]);

  const handleToggleTask = async (task: MovementTaskView) => {
    const newStatus = task.status === ProcessTaskStatus.Completed
      ? ProcessTaskStatus.Pending
      : ProcessTaskStatus.Completed;
    try {
      const result = await updateMovementTaskStatus(task.id, { status: newStatus });
      if (!result.errors || result.errors.length === 0) {
        // Optimistic-looking but server-confirmed: reconcile with the returned row when the API gives
        // one back, so the rendered status is the persisted status rather than what we assumed.
        const persisted = result.payload;
        setTasks(prev => prev.map(x => x.id === task.id
          ? { ...x, ...(persisted && persisted.id === task.id ? persisted : {}), status: persisted?.status ?? newStatus }
          : x));
      }
    } catch {
      // silently fail
    }
  };

  if (!isOpen || !movement) return null;

  const completedCount = tasks.filter(t => t.status === ProcessTaskStatus.Completed).length;
  const percentage = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
  const currency = movement.currencyCode || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose}>
      <div
        className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Movement Details')}</h3>
            <p className="text-sm text-gray-600 mt-1">{movement.employeeName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Movement Info */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <span className="text-xs text-gray-500">{t('Movement Type')}</span>
              <div className="text-sm font-medium text-gray-900">{movement.movementTypeName || t('N/A')}</div>
            </div>
            <div>
              <span className="text-xs text-gray-500">{t('Effective Date')}</span>
              <div className="text-sm font-medium text-gray-900">
                {movement.effectiveDate ? appDate(movement.effectiveDate) : t('N/A')}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">{t('Template')}</span>
              <div className="text-sm font-medium text-gray-900">{movement.templateName || t('N/A')}</div>
            </div>
          </div>

          {/* Position Change */}
          <div className="bg-muted border border-border rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">{t('Changes')}</h5>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('Position')}</span>
                  <div className="text-sm text-gray-900">{movement.currentPosition || t('N/A')}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 mx-3 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('New Position')}</span>
                  <div className="text-sm font-medium text-gray-900">{movement.newPosition || t('N/A')}</div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('Department')}</span>
                  <div className="text-sm text-gray-900">{movement.currentDepartmentName || t('N/A')}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 mx-3 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs text-gray-500">{t('New Department')}</span>
                  <div className="text-sm font-medium text-gray-900">{movement.newDepartmentName || t('N/A')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Reason */}
          {movement.reason && (
            <div>
              <span className="text-xs text-gray-500">{t('Reason')}</span>
              <div className="text-sm text-gray-900 mt-1">{movement.reason}</div>
            </div>
          )}

          {/* Rejection outcome — the approver is forced to type an explanation on reject and the
              server stores it; this is the only place the affected employee can read it. Mirrors the
              leave-request pattern (reviewer name + reviewer comments in the details modal). */}
          {(movement.rejectionReason || movement.rejectedByName) && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-4">
              <h5 className="text-sm font-medium text-red-800 mb-2">{t('Rejection Reason')}</h5>
              {movement.rejectionReason && (
                <p className="text-sm text-red-700 whitespace-pre-line">{movement.rejectionReason}</p>
              )}
              {movement.rejectedByName && (
                <p className="text-xs text-red-700 mt-2">
                  {t('Reviewed by')}: <span className="font-medium">{movement.rejectedByName}</span>
                </p>
              )}
            </div>
          )}

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{t('Progress')}</span>
              {/* Same rule as the checklist below: do not assert "0 / 0 (0%)" before the read lands. */}
              <span className="text-sm text-gray-600" data-testid="movement-progress">
                {tasks.length === 0 && isLoadingTasks
                  ? t('Loading...')
                  : `${completedCount} / ${tasks.length} (${percentage}%)`}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-primary h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>

          {/* Task Checklist */}
          <div className="border-t border-border pt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">{t('Movement Checklist')}</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {/* "Empty" is only claimed after a SUCCESSFUL read. In flight → spinner; failed → an
                  explicit failure with a retry. Never a silent "No checklist items." */}
              {tasks.length === 0 && isLoadingTasks ? (
                <div className="text-center py-8 text-gray-500" data-testid="movement-checklist-loading">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">{t('Loading checklist...')}</p>
                </div>
              ) : tasks.length === 0 && loadFailed ? (
                <div className="text-center py-8 text-gray-500" data-testid="movement-checklist-error">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                  <p className="text-sm">{t('Could not load the checklist.')}</p>
                  <button
                    type="button"
                    onClick={() => loadTasks(movement.id)}
                    className="mt-2 text-sm font-medium text-primary hover:underline"
                  >
                    {t('Retry')}
                  </button>
                </div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('No checklist items.')}</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} className="flex items-start space-x-3 p-3 bg-card border border-border rounded-lg hover:border-gray-300 transition-colors">
                    <button
                      type="button"
                      onClick={() => handleToggleTask(task)}
                      className="mt-0.5 flex-shrink-0"
                      title={task.status === ProcessTaskStatus.Completed ? t('Mark as pending') : t('Mark as completed')}
                    >
                      {task.status === ProcessTaskStatus.Completed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400 hover:text-green-500 transition-colors" />
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${task.status === ProcessTaskStatus.Completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {task.name}
                      </div>
                      <div className="flex items-center flex-wrap gap-2 mt-1">
                        {task.typeName && (
                          <span className="text-xs text-gray-600">{task.typeName}</span>
                        )}
                        {task.dueDate && (
                          <span className="text-xs text-gray-500">{t('Due')}: {appDate(task.dueDate)}</span>
                        )}
                        {task.mandatory && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{t('Required')}</span>
                        )}
                      </div>
                    </div>

                    <span className={`px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${getStatusColor(task.status)}`}>
                      {t(getStatusLabel(task.status))}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              {t('Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovementDetailsModal;
